import { Component } from "react";
import type { ErrorInfo, ReactNode } from "react";
import { Container, Typography, Box, Button, Alert } from "@mui/material";
import { Link as RouterLink } from "react-router-dom";

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
}

interface State {
  hasError: boolean;
  error?: Error;
  errorInfo?: ErrorInfo;
}

export class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error: Error): State {
    // Update state so the next render will show the fallback UI
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    // Log error details for debugging
    console.error("ErrorBoundary caught an error:", error, errorInfo);
    this.setState({
      error,
      errorInfo,
    });
  }

  private handleReset = () => {
    this.setState({ hasError: false, error: undefined, errorInfo: undefined });
  };

  render() {
    if (this.state.hasError) {
      // Custom fallback UI
      if (this.props.fallback) {
        return this.props.fallback;
      }

      // Default error UI
      return (
        <Container maxWidth="lg">
          <Box
            sx={{
              py: 8,
              textAlign: "center",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: 3,
            }}
          >
            <Alert severity="error" sx={{ mb: 2 }}>
              Something went wrong
            </Alert>

            <Typography variant="h2" component="h1" gutterBottom>
              Oops! An Error Occurred
            </Typography>

            <Typography variant="body1" sx={{ mb: 4, maxWidth: 600 }}>
              We apologize for the inconvenience. An unexpected error has
              occurred. Please try refreshing the page or return to the home
              page.
            </Typography>

            <Box
              sx={{
                display: "flex",
                gap: 2,
                flexWrap: "wrap",
                justifyContent: "center",
              }}
            >
              <Button
                variant="contained"
                color="primary"
                onClick={this.handleReset}
                size="large"
              >
                Try Again
              </Button>

              <Button
                component={RouterLink}
                to="/"
                variant="outlined"
                color="primary"
                size="large"
              >
                Return to Home
              </Button>
            </Box>

            {process.env.NODE_ENV === "development" && this.state.error && (
              <Box sx={{ mt: 4, textAlign: "left", maxWidth: "100%" }}>
                <Typography variant="h6" gutterBottom>
                  Error Details (Development Only):
                </Typography>
                <Box
                  component="pre"
                  sx={{
                    backgroundColor: "grey.100",
                    p: 2,
                    borderRadius: 1,
                    overflow: "auto",
                    fontSize: "0.875rem",
                    fontFamily: "monospace",
                  }}
                >
                  {this.state.error.toString()}
                  {this.state.errorInfo?.componentStack}
                </Box>
              </Box>
            )}
          </Box>
        </Container>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
